import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../utils/rtk-headers';
import { getEnvironment } from '../env/env';
import {
  AppBaseQueryError,
  getStoredUser,
  renewSession,
  withFriendlyServerError,
} from 'src/services/baseQueryAuth';

// Sign-in requests opt out of the refresh flow: a 401 there means wrong credentials.
type SsoArgs = FetchArgs & { meta?: { skipAuthRetry?: boolean } };

let isRefreshing = false;

const getRefreshToken = () => {
  const user = getStoredUser();

  return user.refresh_token || user.refresh;
};

const baseQueryWithAuth: BaseQueryFn<SsoArgs, unknown, AppBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const environment = await getEnvironment();
  const baseSSOUrl = environment.APP_SSO_BASE_URL;
  const urlPath = `/api/${args.url}`;
  const url = new URL(urlPath, baseSSOUrl);
  const updatedArgs = { ...args, url: url.toString() };
  const result = await fetchBaseQuery({
    baseUrl: baseSSOUrl,
    prepareHeaders,
  })(updatedArgs, api, extraOptions);

  if (result.error?.status === 400 || result.error?.status === 404) {
    retry.fail(result.error);
  }

  if (result.error?.status === 401 && !updatedArgs?.meta?.skipAuthRetry && !isRefreshing) {
    isRefreshing = true;
    let isRenewed = false;

    try {
      isRenewed = await renewSession(getRefreshToken());
    } catch (error) {
      console.log('staggeredBaseQueryWithBailOut error', error);
    } finally {
      isRefreshing = false;
    }

    // A renewed session falls through so retry() repeats the request with the new token.
    if (!isRenewed) {
      retry.fail(result.error);
    }
  }

  return withFriendlyServerError(result);
};

// Annotated for the same reason as in base.api.ts: retry()'s RetryOptions union would make
// `extraOptions` required on every endpoint definition.
const staggeredBaseQueryWithBailOut: BaseQueryFn<
  SsoArgs,
  unknown,
  AppBaseQueryError,
  { maxRetries?: number }
> = retry(baseQueryWithAuth, {
  maxRetries: 2,
});

export const baseSsoApi = createApi({
  reducerPath: 'baseSsoApi',
  baseQuery: staggeredBaseQueryWithBailOut,
  tagTypes: [],
  endpoints: () => ({}),
});
